import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';

import type { DeviceAction, DeviceActionUI } from 'src/types/DeviceActionUI';
import { is_enable_disable_scene_action_ui, is_play_scene_action_ui } from 'src/types/SceneActionUI';

import { is_notification_action_ui } from 'src/types/NotificationActionUI';
import { createHexToDecimalOrId } from 'src/helpers/utilities';
import { isZendure, isDiffuser, isPill, isFanDevice } from 'src/helpers/interface_helpers';

import { createGuiDeviceId } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
import { is_device_action_ui } from 'src/types/DeviceActionUI';
import { prepareVCActionToIots } from 'src/components/Scenes/helpers/virtual_components/virtual_device_actions';
import { isVCType } from 'src/components/Scenes/helpers/virtual_components/virtual_component_utils';

import type { SceneActionUI } from 'src/types/SceneActionUI';
import type { NotificationActionUI } from 'src/types/NotificationActionUI';

// Device-specific action preparation helpers
import type {
	DeviceActionContext,
	PreparedDeviceAction,
	DeviceActionStrategyArgs,
	DeviceActionStrategyResult,
	NotificationPreparer,
} from 'src/components/Scenes/helpers/device_actions/types';
import {
	isRelayLikeMode,
	prepareRelayAction,
} from 'src/components/Scenes/helpers/device_actions/relay_actions';
import {
	isColorBulb,
	isCCTMode,
	prepareColorBulbAction,
	prepareCCTBulbAction,
} from 'src/components/Scenes/helpers/device_actions/bulb_actions';
import { prepareDimmerAction } from 'src/components/Scenes/helpers/device_actions/dimmer_actions';
import { prepareZendureAction } from 'src/components/Scenes/helpers/device_actions/custom_cases/zendure_actions';
import { prepareMediaPlayerAction } from 'src/components/Scenes/helpers/device_actions/custom_cases/media_player_actions';
import { prepareCuryAction } from 'src/components/Scenes/helpers/device_actions/custom_cases/cury_actions';
import { prepareCameraAction } from 'src/components/Scenes/helpers/device_actions/custom_cases/camera_actions';
import { isCameraActionName } from 'src/components/Scenes/helpers/camera_action_helpers';
import { prepareFanAction } from 'src/components/Scenes/helpers/device_actions/custom_cases/fan_actions';

// -------------- ACTION HELPERS --------------

/**
 * Strategy definition declaring how a particular device category prepares its scene action payload.
 */
interface DeviceActionStrategy {
	name: string;
	canHandle: (args: DeviceActionStrategyArgs) => boolean;
	prepare: (args: DeviceActionStrategyArgs) => DeviceActionStrategyResult;
	postProcess?: (
		result: Exclude<DeviceActionStrategyResult, null | undefined>,
		args: DeviceActionStrategyArgs,
	) => DeviceActionStrategyResult;
}

/**
 * Applies custom timer metadata (value and unit) when the originating action
 * includes a timer. Shared by relay-like and light devices so both can use the
 * "Timer custom settings" option in scene change-state actions (FE-1563).
 *
 * @param action - Prepared device action to decorate.
 * @param element - Source UI action that may include timer data.
 * @returns The same action reference, updated with timer fields when present.
 */
const applyActionTimer = (
	action: PreparedDeviceAction,
	element: DeviceActionUI,
): PreparedDeviceAction => {
	if (Number(element?.timer?.value) > 0) {
		action.turntimer = Number(element.timer.value);
		action.turntimer_unit = element.timer.units;
	}

	return action;
};

/**
 * Ordered collection of strategies used to prepare device actions. Evaluation stops at the first matching strategy.
 */
const DEVICE_ACTION_STRATEGIES: DeviceActionStrategy[] = [
	{
		name: 'virtual-component',
		canHandle: ({ primaryAction }) => isVCType(primaryAction?.name),
		prepare: ({ element, context, deviceID, hexToDecimal }) => {
			if (import.meta.env.DEV) {
				console.log('Action To IOTS: Virtual component action', element);
			}

			// For XT devices, hexToDecimal might be a string ID that can't be converted to number
			// Try to convert to number, but if it fails, use the original value (for XT devices)
			let decimalId: number | string;
			if (typeof hexToDecimal === 'number') {
				decimalId = hexToDecimal;
			} else {
				const converted = Number(hexToDecimal);
				decimalId = Number.isFinite(converted) ? converted : hexToDecimal;
			}

			// If it's still not a valid number or string, fail
			if (typeof decimalId !== 'number' && typeof decimalId !== 'string') {
				return undefined;
			}

			return prepareVCActionToIots(deviceID, decimalId, context.actions);
		},
	},
	{
		name: 'pill',
		canHandle: ({ type }) => isPill(type),
		prepare: ({ context }) => prepareRelayAction(context),
	},
	{
		name: 'relay-like',
		canHandle: ({ mode }) => isRelayLikeMode(mode),
		prepare: ({ context }) => prepareRelayAction(context),
		postProcess: (result, { element }) => applyActionTimer(result as PreparedDeviceAction, element),
	},
	{
		name: 'zendure',
		canHandle: ({ type }) => isZendure(type),
		prepare: ({ context }) => prepareZendureAction(context),
	},
	{
		name: 'cury-diffuser',
		canHandle: ({ type }) => isDiffuser(type),
		prepare: ({ context }) => prepareCuryAction(context),
	},
	{
		name: 'fan',
		canHandle: ({ type }) => isFanDevice(type),
		prepare: ({ context }) => prepareFanAction(context),
	},
	{
		name: 'media-player',
		canHandle: ({ mode }) => mode === 'media_player',
		prepare: ({ context }) => prepareMediaPlayerAction(context),
	},
	{
		name: 'color-bulb',
		canHandle: ({ type, mode, context }) => isColorBulb(type, mode, context.channelMode),
		prepare: ({ context }) => prepareColorBulbAction(context),
		postProcess: (result, { element }) => applyActionTimer(result as PreparedDeviceAction, element),
	},
	{
		name: 'cct-bulb',
		canHandle: ({ type, mode, context }) => isCCTMode(type, context.channelMode, mode),
		prepare: ({ context }) => prepareCCTBulbAction(context),
		postProcess: (result, { element }) => applyActionTimer(result as PreparedDeviceAction, element),
	},
	{
		name: 'camera',
		canHandle: ({ primaryAction }) => isCameraActionName(primaryAction?.name),
		prepare: ({ context }) => prepareCameraAction(context),
	},
	{
		name: 'dimmer-default',
		canHandle: () => true,
		prepare: ({ context }) => prepareDimmerAction(context),
		postProcess: (result, { element }) => applyActionTimer(result as PreparedDeviceAction, element),
	},
];

/**
 * Wraps an action in a delay scaffold understood by IoT scripts.
 *
 * @param obj - Prepared action payload to execute after the delay.
 * @param delayData - Delay descriptor coming from the UI (value, units, etc.).
 * @param more - Optional metadata to merge into the delayed action (used by notifications).
 * @returns Delayed action object ready for IoT script consumption.
 */
const delay_action = (obj, delayData, more?) => {
	let objDelayedAction = {
		wait: delayData.value,
		wid: Date.now(),
		do: {
			blk: [obj],
		},
		//?!!!JEMMS are executes with sec and minutes!! ?!!
		units: delayData.units === 'min' ? 'minutes' : delayData.units,
	};

	if (more !== undefined) {
		objDelayedAction = {
			...objDelayedAction,
			do: {
				blk: [
					{
						...obj,
						msg_type: more.msg_type,
					},
				],
			},
		};

		return {
			...objDelayedAction,
			_gui_function: more._gui_function,
			msg_type: more.msg_type,
		};
	}

	return objDelayedAction;
};

/**
 * Creates the base notification payload shared by all notification types.
 */
const createBaseNotification = (act: NotificationActionUI): Record<string, unknown> => ({
	notify: act.type,
	_gui_type: 'notification',
});

/**
 * Returns a sanitised `toSub` list (sub-unit ids) ready to attach to an IoT
 * notification payload. Coerces values to numbers, drops invalid entries, and
 * deduplicates.
 *
 * `undefined` is returned when the action has no list set, which preserves
 * legacy "all sub-units" behavior on the BE side.
 *
 * FE-1682: per-sub-unit delivery for push/mail notifications. Owner delivery
 * is governed per-action by the sibling `pushSkipUnit` flag, not by this list.
 */
const sanitiseSubUnitList = (toSub: unknown): number[] | undefined => {
	if (!Array.isArray(toSub)) return undefined;
	const seen = new Set<number>();
	const cleaned: number[] = [];
	for (const raw of toSub) {
		const id = Number(raw);
		if (!Number.isFinite(id) || seen.has(id)) continue;
		seen.add(id);
		cleaned.push(id);
	}
	return cleaned;
};

/**
 * Prepares push notification actions.
 */
const preparePushNotification: NotificationPreparer = (act) => {
	const messageType = act.msg_type;
	if (!messageType) {
		return undefined;
	}

	const subUnits = sanitiseSubUnitList(act.toSub);
	const baseWithRecipients = {
		...createBaseNotification(act),
		msg: act.msg || 'My scene triggered!',
		...(subUnits !== undefined ? { toSub: subUnits } : {}),
		...(act.pushSkipUnit === true ? { pushSkipUnit: true } : {}),
	};

	if (!act.delay || Number(act.delay.value) <= 0) {
		return {
			notif: {
				...baseWithRecipients,
				_gui_function: messageType,
				msg_type: messageType,
			},
		};
	}

	return {
		notif: baseWithRecipients,
		delayMeta: {
			_gui_function: messageType,
			msg_type: messageType,
		},
	};
};

/**
 * Prepares mail notification actions.
 */
const prepareMailNotification: NotificationPreparer = (act) => {
	if (!act.msg) {
		return undefined;
	}

	const subUnits = sanitiseSubUnitList(act.toSub);

	return {
		notif: {
			...createBaseNotification(act),
			msg: act.msg,
			...(subUnits !== undefined ? { toSub: subUnits } : {}),
			...(act.pushSkipUnit === true ? { pushSkipUnit: true } : {}),
		},
	};
};

/**
 * Prepares event log notification actions.
 */
const prepareEventLogNotification: NotificationPreparer = (act) => {
	if (act.msg_type !== 'event_log') {
		return undefined;
	}

	return {
		notif: createBaseNotification(act),
	};
};

/**
 * Prepares Alexa notification actions.
 */
const prepareAlexaNotification: NotificationPreparer = (act) => {
	if (!act.msg_type?.match(/alexa_notif/)) {
		return undefined;
	}

	const notif: Record<string, unknown> = {
		...createBaseNotification(act),
		msg: 'alexa',
	};

	if (act.msg_type === 'alexa_notif_premium') {
		notif.type = 'premium';
	}

	return { notif };
};

const NOTIFICATION_PREPARERS: Record<string, NotificationPreparer> = {
	push_notification: preparePushNotification,
	mail_notification: prepareMailNotification,
	event_log: prepareEventLogNotification,
	alexa_notif: prepareAlexaNotification,
};

/**
 * Prepares notification actions (push/email/event log/Alexa) for IoT scripts.
 *
 * @param act - Notification action coming from the UI.
 * @returns Prepared action payload or `undefined` when the action cannot be created.
 */
export const createNotificationAction = (act: NotificationActionUI) => {
	if (!is_notification_action_ui(act)) {
		return undefined;
	}

	const preparer = NOTIFICATION_PREPARERS[act.type];
	if (!preparer) {
		return undefined;
	}

	const preparation = preparer(act);
	if (!preparation) {
		return undefined;
	}

	const { notif, delayMeta } = preparation;

	if (act.delay && Number(act.delay.value) > 0) {
		return delay_action(notif, act.delay, delayMeta);
	}

	return delayMeta ? { ...notif, ...delayMeta } : notif;
};

/**
 * Converts a device action coming from the UI into an IoT-ready payload.
 *
 * Delegates to specialised strategies based on device type/channel mode and adds optional timers/delays.
 *
 * @param element - UI representation of the device action.
 * @param type - Device type identifier.
 * @param _mode - Device operating mode.
 * @returns Prepared IoT action payload or `undefined` when no suitable strategy exists.
 */
export const createDeviceAction = (
	element: DeviceActionUI,
	type: string | undefined,
	_mode: string | undefined,
) => {
	// Validate input parameters
	if (!is_device_action_ui(element) || typeof type === 'undefined') {
		return undefined;
	}

	const rawChannel = element.device.channel ?? 0;
	const channelNumber = Number(rawChannel);
	const channel = Number.isFinite(channelNumber) ? channelNumber : 0;
	const hex_id = element.device.id;
	const deviceID = createGuiDeviceId(element.device);
	const channelMode = get(devices)?.[deviceID]?.mode;
	const hexToDecimal = createHexToDecimalOrId(hex_id);
	const action_arr: Array<DeviceAction> = element.actions || [];
	const _action_el: DeviceAction = action_arr[0]; // First available action element

	// Validate that we have at least one action
	if (!action_arr.length || !_action_el) {
		return undefined;
	}

	// For virtual component actions (XT devices), mode is not required
	// Check if this is a virtual component action before requiring mode
	const isVirtualComponentAction = isVCType(_action_el?.name);

	// Mode is required for non-virtual-component actions
	if (!isVirtualComponentAction && typeof _mode === 'undefined') {
		return undefined;
	}

	// Context object for device-specific preparers
	const context: DeviceActionContext = {
		hexToDecimal,
		channel,
		actions: action_arr,
		mode: _mode,
		type,
		channelMode,
	};

	const strategyArgs: DeviceActionStrategyArgs = {
		element,
		type,
		mode: _mode,
		context,
		primaryAction: _action_el,
		deviceID,
		hexToDecimal,
	};

	let action: DeviceActionStrategyResult;

	// Evaluate strategies in order; stop at the first match to preserve priority.
	for (const strategy of DEVICE_ACTION_STRATEGIES) {
		if (!strategy.canHandle(strategyArgs)) {
			continue;
		}

		const result = strategy.prepare(strategyArgs);
		if (result) {
			action = strategy.postProcess ? strategy.postProcess(result, strategyArgs) : result;
		} else {
			action = result;
		}
		break;
	}

	// Return undefined if action preparation failed
	if (!action) {
		return undefined;
	}

	// Handle delayed actions
	if (element.delay != undefined && Number(element.delay.value) > 0) {
		return delay_action(action, element.delay);
	}

	return action;
};

/**
 * Converts scene-level actions (play scene, enable/disable scene) into IoT script payloads.
 *
 * @param data - Scene action definition from the UI.
 * @returns Prepared action payload or `undefined` when the action is not supported.
 */
export const createSceneAction = (data: SceneActionUI) => {
	let action: any = {};
	if (is_play_scene_action_ui(data)) {
		if (data.option === 'with_if_evaluation') {
			action = {
				_gui_type: 'activate_scene',
				trigger: `${data.scene_id}`,
			};
		} else {
			action = {
				_gui_type: 'activate_scene',
				trigger: `${data.scene_id}`,
				skipif: true,
			};
		}
	} else if (is_enable_disable_scene_action_ui(data)) {
		const { scene_id, option } = data;

		const mapActions = {
			action_enable_scene: 'true',
			action_disable_scene: 'false',
			action_toggle_scene: 'toggle',
		};
		action = {
			_gui_type: 'enable_disable_scene',
			r: `blprop:${scene_id}:enabled`,
			set: String(mapActions[option]),
		};
	} else {
		action = undefined;
	}

	if (action !== undefined) {
		if (data.delay != undefined && Number(data.delay.value) > 0) {
			return delay_action(action, data.delay);
		}
		return action;
	} else {
		return undefined;
	}
};

/**
 * Converts group actions into IoT script payloads.
 *
 * @param data - Group action data as configured in the UI.
 * @returns Prepared group action payload, possibly wrapped in a delay.
 */
export const createGroupAction = (data) => {
	//{grpctrl:<group_id_num>, turn:"on|off"}
	let action = {};
	action['grpctrl'] = Number(data.group);
	action[data.actions[0].name] = data.actions[0].value;
	action['_gui_type'] = 'group_action';

	/**because always comes the default UI
	 * delay_action= {units: 'sec', value:0}
	 * we need to check value
	 */

	if (data.delay != undefined && Number(data.delay.value) > 0) {
		return delay_action(action, data.delay);
	}
	return action;
};
